"use client";

import { useState } from "react";
import { useEffect } from "react";
import Link from "next/link";
import { Card } from "@/components/ui/Card";
import PublicLayout from "@/components/layout/PublicLayout";
import { CheckCircle, AlertCircle, Mail } from "lucide-react";

declare global {
  interface Window {
    grecaptcha: any;
  }
}

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [recaptchaReady, setRecaptchaReady] = useState(false);

  useEffect(() => {
    const siteKey = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY;
    if (siteKey && !siteKey.includes("YOUR_RECAPTCHA")) {
      const existing = document.querySelector('script[src="https://www.google.com/recaptcha/api.js"]');
      if (existing) {
        setRecaptchaReady(true);
        return;
      }
      const script = document.createElement("script");
      script.src = `https://www.google.com/recaptcha/api.js?render=${siteKey}`;
      script.async = true;
      script.defer = true;
      script.onload = () => {
  window.grecaptcha.ready(() => {
    setRecaptchaReady(true);
  });
};
      document.head.appendChild(script);
    } else {
      setRecaptchaReady(true);
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email.trim()) {
      setError("Please enter your email address");
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const siteKey = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY;
      let recaptchaToken = "";
      if (siteKey && !siteKey.includes("YOUR_RECAPTCHA")) {
        if (!window.grecaptcha) {
          setError("Security verification not ready. Please try again.");
          setLoading(false);
          return;
        }
        recaptchaToken = await window.grecaptcha.execute(siteKey, { action: "forgot_password" });
      }

      const response = await fetch("/api/auth/forgot-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, recaptchaToken }),
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || "Failed to process request");
        setLoading(false);
        return;
      }

      setSuccess("Password reset link has been sent to your email");
      setSubmitted(true);
      setEmail("");
    } catch (error) {
      console.error("Forgot password error:", error);
      setError("An error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <PublicLayout>
      <div className="min-h-screen bg-linear-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <div className="p-8">
            {/* Header */}
            <div className="mb-8">
              <div className="flex justify-center mb-4">
                <div className="w-14 h-14 bg-blue-600 rounded-lg flex items-center justify-center">
                  <Mail size={28} className="text-white" />
                </div>
              </div>
              <h1 className="text-2xl font-bold text-center text-slate-900">Forgot Password?</h1>
              <p className="text-slate-600 text-center text-sm mt-2">
                Enter your email and we'll send you a link to reset it
              </p>
            </div>

            {submitted && success ? (
              <div className="space-y-6">
                <div className="flex justify-center">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                    <CheckCircle size={32} className="text-green-600" />
                  </div>
                </div>
                <div>
                  <p className="text-center text-slate-700 font-medium">{success}</p>
                  <p className="text-center text-slate-600 text-sm mt-2">
                    Check your email for a link to reset your password. If it doesn't appear, check your spam folder.
                  </p>
                </div>
                <Link
                  href="/login"
                  className="block text-center bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-lg font-bold transition-all"
                >
                  Back to Login
                </Link>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                {error && (
                  <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-lg">
                    <AlertCircle size={20} className="text-red-600 shrink-0" />
                    <p className="text-red-700 text-sm">{error}</p>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-semibold text-slate-900 mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading || !recaptchaReady}
                  className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-slate-400 text-white py-3 rounded-lg font-bold transition-all flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Sending...
                    </>
                  ) : (
                    "Send Reset Link"
                  )}
                </button>

                <div className="text-center">
                  <p className="text-slate-600 text-sm">
                    Remember your password?{" "}
                    <Link href="/login" className="text-blue-600 hover:underline font-medium">
                      Back to Login
                    </Link>
                  </p>
                </div>
              </form>
            )}
          </div>
        </Card>
      </div>
    </PublicLayout>
  );
}
